import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../config";
import { AuthImage, Button, Checkbox, Panel, StatusPill } from "./ui";

// Human review. The only place in Robo that can mark an answer approved.
//
// Machine QA is shown alongside, clearly labelled as a machine opinion — "no
// concerns detected" is information, not permission. Nothing here approves itself.

const MACHINE = {
  no_concerns:        ["green", "No Concerns"],
  review_recommended: ["amber", "Review recommended"],
  likely_issue:       ["red",   "Likely issue"],
};

const QuestionCard = ({ question, paper, onChanged, expanded, onToggle, onResolve, onCheck, running, selected, onSelect }) => {
  const [answer, setAnswer]           = useState(question.answer || "");
  const [explanation, setExplanation] = useState(question.explanation || "");
  // One per line: this is the list the student is actually matched against, and it
  // was previously invisible during review — you could approve a question without
  // ever seeing which wordings would be accepted.
  const [variants, setVariants] = useState((question.answers || []).join("\n"));
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  const [machineTone, machineLabel] = MACHINE[question.machine_review_status] || ["slate", "Not yet checked"];
  const approved = question.human_review_status === "approved";
  const probabilistic = question.grading_mode === "probabilistic";
  // The answer changed after the last machine check — or was never checked — so the
  // verdict on screen does not describe the answer on screen.
  const needsCheck = question.needs_quality_check;
  // Quick approval is for the uneventful ones only: a clean machine verdict, an answer
  // actually present, and nothing the solver gave up on.
  const cleanForQuickApprove =
    !question.needs_quality_check &&
    question.machine_review_status === "no_concerns" &&
    question.solvable !== false &&
    !question.unsupported &&
    String(question.answer || "").trim().length > 0;
  const parent   = (paper.questions || []).find((q) => q.number === question.parent_number);
  const assets   = (paper.assets || []).filter((a) => (question.asset_ids || []).includes(a.asset_id));

  const edited = answer !== (question.answer || "")
    || explanation !== (question.explanation || "")
    || variants !== (question.answers || []).join("\n");

  // The card does not remount when its question changes — it is keyed by number — so
  // local edits would go stale after a retry re-solves the question underneath it, and
  // approving without opening would write the old answer back over the new one.
  // Re-sync whenever the stored values change and there is nothing unsaved to lose.
  useEffect(() => {
    if (edited) return;
    setAnswer(question.answer || "");
    setExplanation(question.explanation || "");
    setVariants((question.answers || []).join("\n"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.answer, question.explanation, (question.answers || []).join("\n")]);

  const submit = async (status) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/api/admin/robo/papers/${paper.id}/questions/${encodeURIComponent(question.number)}`,
        {
          humanReviewStatus: status,
          answer,
          explanation,
          answers: variants.split("\n").map((v) => v.trim()).filter(Boolean),
        }
      );
      if (!data.success) throw new Error(data.error || "Could not save.");
      onChanged(data.paper);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-xl border transition-colors ${
      approved   ? "border-emerald-500/30 bg-emerald-500/[0.06]"
      : needsCheck ? "border-rose-500/35 bg-rose-500/[0.06]"
                   : "border-white/10 bg-white/[0.03]"
    }`}>
      {/* The toggle and the quick approve are siblings, not nested: a button inside a
          button is invalid, and clicking approve must not also expand the card. */}
      <div className="flex items-center gap-3 px-4 py-3">
        {onSelect && (
          <span className="-ml-2 shrink-0">
            <Checkbox
              checked={selected}
              onChange={() => onSelect(question.number)}
              title={`Select question ${question.number}`}
            />
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="w-16 shrink-0 text-sm font-semibold text-white">Q{question.number}</span>
          <span className="min-w-0 flex-1 truncate text-xs text-white/55">{question.content}</span>
          {assets.length > 0 && <StatusPill color="violet">image</StatusPill>}
          {needsCheck
            ? <StatusPill color="rose">Needs Quality Check</StatusPill>
            : <StatusPill color={machineTone}>{machineLabel}</StatusPill>}
          {question.unsupported ? <StatusPill color="slate">Not Supported</StatusPill>
            : approved ? <StatusPill color="green">Approved</StatusPill>
                       : <StatusPill color="slate">Pending</StatusPill>}
        </button>

        {/* Only for answers machine QA had nothing to say about. Anything it flagged has
            to be opened and read before it can be approved — the whole point of the
            verdict is to decide where attention goes. */}
        {!approved && !question.unsupported && (
          <Button
            variant="success"
            size="sm"
            className="shrink-0"
            disabled={saving || !cleanForQuickApprove}
            onClick={() => submit("approved")}
            title={
              cleanForQuickApprove
                ? "Approve without opening — machine QA raised nothing"
                : "Open this one and read it: machine QA had something to say"
            }
          >
            {saving ? "Saving…" : "Approve"}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-white/10 px-4 py-4">
          {parent?.content && (
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-white/35">Parent context — Q{parent.number}</p>
              <p className="whitespace-pre-line rounded-lg bg-white/5 px-3 py-2 text-xs leading-relaxed text-white/65">
                {parent.content}
              </p>
            </div>
          )}

          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-white/35">Original question</p>
            <p className="whitespace-pre-line text-sm leading-relaxed text-white/85">{question.content}</p>
            {(question.options || []).length > 0 && (
              <ul className="mt-2 space-y-1">
                {question.options.map((option, i) => (
                  <li key={i} className="text-xs text-white/60">• {option}</li>
                ))}
              </ul>
            )}
          </div>

          {assets.map((asset) => (
            <div key={asset.asset_id}>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-white/35">
                {asset.type} · page {asset.source_page}
              </p>
              {asset.preview_url ? (
                <AuthImage
                  src={`${API_BASE_URL}${asset.preview_url}`}
                  alt={`Visual for question ${question.number}`}
                  className="max-h-64 rounded-lg border border-white/10 bg-black/30 object-contain"
                  emptyLabel="Could not load this question's visual"
                />
              ) : (
                <p className="text-xs text-amber-300">This question needs a visual that has not been produced yet.</p>
              )}
            </div>
          ))}

          {(question.machine_concerns || []).length > 0 && (
            <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2.5">
              <p className="mb-1 text-[10px] uppercase tracking-wider text-amber-300/80">Machine QA raised</p>
              <ul className="space-y-1">
                {question.machine_concerns.map((concern, i) => (
                  <li key={i} className="text-xs leading-relaxed text-amber-100/80">
                    <span className="font-semibold">{concern.type}:</span> {concern.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {question.unsupported && (
            <div className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wider text-white/35">Set aside as not supported</p>
              <p className="mt-1 text-xs leading-relaxed text-white/65">
                {question.unsupported_reason || "No reason recorded."}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-white/40">
                It stays on the paper and is recorded in the JSON, but never becomes a
                question a student sees.
              </p>
            </div>
          )}

          {question.solvable === false && (
            <div className="rounded-lg border border-red-500/25 bg-red-500/[0.07] px-3 py-2.5">
              <p className="text-xs leading-relaxed text-red-200/90">
                The solver could not answer this: {question.unsolvable_reason || "no reason given"}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-red-200/60">
                If this is not gradable by typing at all — a table to tick, a diagram to
                label — use <span className="font-semibold">Not Supported</span>. Approving
                it would publish a question with no answer.
              </p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-white/35">
              {probabilistic ? "Mark scheme — what the AI grades against" : "Generated answer"}
            </label>
            {probabilistic && (
              <p className="mb-1.5 text-[10px] leading-relaxed text-violet-300/70">
                Not an answer to match. This is the rubric: the creditable points and how
                the marks are awarded. Students never see it.
              </p>
            )}
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-[#2e92b0]"
            />
          </div>

          {/* Deterministic questions only: on a probabilistic one the answer field holds
              the mark scheme and there is no variant list to match against. */}
          {!probabilistic && (
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-white/35">
                Also accepted — one per line ({variants.split("\n").filter((v) => v.trim()).length})
              </label>
              <textarea
                value={variants}
                onChange={(e) => setVariants(e.target.value)}
                rows={4}
                placeholder="Other wordings a student might type"
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 font-mono text-xs text-white outline-none focus:border-[#2e92b0]"
              />
              <p className="mt-1 text-[10px] leading-relaxed text-white/35">
                A student's typed answer is accepted if it matches any line here. If these
                are different answers rather than different wordings of one answer, the
                question should be open-ended instead.
              </p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-white/35">Explanation</label>
            <textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm leading-relaxed text-white outline-none focus:border-[#2e92b0]"
            />
          </div>

          {error && <p className="text-xs text-red-300">{error}</p>}

          <div className="flex flex-wrap gap-2">
            {/* A set-aside question offers one action: undo. Approving it, re-solving it
                or checking it would all be work on something that will never ship. */}
            {question.unsupported ? (
              <Button
                variant="success"
                disabled={saving}
                onClick={() => submit("pending")}
                title="Put this question back into the review queue"
              >
                Bring Back
              </Button>
            ) : (
            <>
            <Button
              variant="success"
              disabled={saving || needsCheck}
              onClick={() => submit("approved")}
              title={needsCheck
                ? "Run Quality Check on this question first — the answer has changed since it was last checked"
                : undefined}
            >
              {edited ? "Edit & Approve" : "Approve"}
            </Button>
            {/* A disposition rather than a verdict: the question is real, this app just
                cannot grade it. Recorded in the JSON's _meta, kept out of the bank, and
                no longer counted as outstanding work. */}
            <Button
              disabled={saving}
              onClick={() => submit("unsupported")}
              title="This app cannot grade this question — record it and move on"
            >
              Not Supported
            </Button>
            {onResolve && (
              <Button
                disabled={saving || running}
                onClick={() => onResolve(question.number, question.group)}
                title={
                  running
                    ? "Available once the running step finishes — only one stage can run at a time"
                    : (question.machine_concerns || []).length > 0
                      ? "Re-answer this question, telling Claude what the review found wrong"
                      : "Generate a fresh answer for this question"
                }
              >
                Resolve This Q
              </Button>
            )}
            {onCheck && (
              // Separate from Resolve so an answer you edited by hand can be checked
              // without regenerating it, and so one question can be re-checked without
              // paying for machine QA across the whole paper.
              <Button
                disabled={saving || running || !question.answer}
                onClick={() => onCheck(question.number)}
                title={
                  running
                    ? "Available once the running step finishes"
                    : "Run machine QA on just this question"
                }
              >
                Quality Check This Q
              </Button>
            )}
            </>
            )}
            <span className="self-center text-[11px] text-white/35">
              {question.module_name} · {question.chapter} · {question.question_type}
              {" · "}
              <span className={probabilistic ? "font-semibold text-violet-300" : "font-semibold text-sky-300"}>
                {probabilistic ? "AI graded" : "Exact match"}
              </span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewPanel = ({ paper, onChanged, onResolveQuestion, onCheckQuestion, onResolveMany, onCheckMany, running, highlight, autoOpen }) => {
  const [filter, setFilter] = useState("pending");
  const [selectedNumbers, setSelectedNumbers] = useState([]);

  const toggleSelected = (number) =>
    setSelectedNumbers((current) =>
      current.includes(number) ? current.filter((n) => n !== number) : [...current, number]
    );
  const [openNumber, setOpenNumber] = useState(null);

  const answerable = useMemo(
    () => (paper.questions || []).filter((q) => q.answerable && !q.unsupported),
    [paper.questions]
  );

  // Set aside as ungradable. Kept out of every count, because they are a recorded
  // decision rather than outstanding work — but reachable, so the decision can be
  // read back and reversed. Without this, Not Supported is a one-way door.
  const setAside = useMemo(
    () => (paper.questions || []).filter((q) => q.answerable && q.unsupported),
    [paper.questions]
  );

  const counts = useMemo(() => ({
    all:       answerable.length,
    needsCheck: answerable.filter((q) => q.needs_quality_check).length,
    pending:   answerable.filter((q) => q.human_review_status !== "approved").length,
    approved:  answerable.filter((q) => q.human_review_status === "approved").length,
    needs:     answerable.filter((q) => q.machine_review_status === "review_recommended").length,
    critical:  answerable.filter((q) => q.machine_review_status === "likely_issue" || q.solvable === false).length,
    hasImage:  answerable.filter((q) => (q.asset_ids || []).length > 0).length,
    imageWork: answerable.filter((q) => (q.asset_ids || []).some((id) => {
      const asset = (paper.assets || []).find((a) => a.asset_id === id);
      return !asset || asset.human_review_status !== "approved";
    })).length,
  }), [answerable, paper.assets]);

  // The set-aside list is a different source, not a subset — those questions are
  // deliberately absent from `answerable` and every count derived from it.
  const visible = (filter === "setAside" ? setAside : answerable).filter((q) => {
    switch (filter) {
      case "setAside":  return true;
      case "needsCheck": return q.needs_quality_check;
      case "pending":   return q.human_review_status !== "approved";
      case "approved":  return q.human_review_status === "approved";
      case "needs":     return q.machine_review_status === "review_recommended";
      case "critical":  return q.machine_review_status === "likely_issue" || q.solvable === false;
      case "hasImage":  return (q.asset_ids || []).length > 0;
      case "imageWork": return (q.asset_ids || []).some((id) => {
        const asset = (paper.assets || []).find((a) => a.asset_id === id);
        return !asset || asset.human_review_status !== "approved";
      });
      default: return true;
    }
  });

  const allShownSelected  = visible.length > 0 && visible.every((q) => selectedNumbers.includes(q.number));
  const someShownSelected = visible.some((q) => selectedNumbers.includes(q.number));
  const hasSelection      = selectedNumbers.length > 0;

  const filters = [
    ["all", "All", counts.all],
    ["needsCheck", "Needs Quality Check", counts.needsCheck],
    ["pending", "Pending", counts.pending],
    ["approved", "Approved", counts.approved],
    ["setAside", "Not Supported", setAside.length],
    ["needs", "Needs Review", counts.needs],
    ["critical", "Critical", counts.critical],
    ["hasImage", "Has Image", counts.hasImage],
    ["imageWork", "Image Review Needed", counts.imageWork],
  ];

  return (
    <Panel
      highlight={highlight}
      collapsible
      autoOpen={autoOpen}
      // Same rule as the other sections: open only while questions are outstanding.
      defaultOpen={counts.pending > 0}
      title="Human Review"
      subtitle={
        counts.needsCheck > 0
          ? `${counts.approved} of ${counts.all} approved · ${counts.needsCheck} waiting on a quality check first`
          : `${counts.approved} of ${counts.all} approved — only you can approve an answer`
      }
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {filters.map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${
              filter === key
                ? "border-[#2e92b0] bg-[#2e92b0]/20 text-white"
                : "border-white/15 bg-white/5 text-white/60 hover:bg-white/10"
            }`}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      {/* One row. The select-all sits on the left and the actions arrive on the right
          when there is something to act on, rather than a second bar appearing above
          and shifting the list down. The row keeps its height either way. */}
      {visible.length > 0 && filter !== "setAside" && (
        <div className={`mb-3 -mx-1 flex flex-wrap items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors ${
          hasSelection
            ? "border-[#2e92b0]/30 bg-[#2e92b0]/[0.08]"
            : "border-transparent"
        }`}>
          <Checkbox
            checked={allShownSelected}
            indeterminate={someShownSelected}
            onChange={() => setSelectedNumbers(allShownSelected ? [] : visible.map((q) => q.number))}
            label={hasSelection
              ? `${selectedNumbers.length} of ${visible.length} selected`
              : `Select all ${visible.length}`}
            title="Select every question currently shown by this filter"
          />

          {hasSelection && (
            <div className="ml-auto flex flex-wrap items-center gap-1.5">
              <Button
                size="sm"
                disabled={running}
                onClick={() => { onResolveMany(selectedNumbers); setSelectedNumbers([]); }}
                title="Re-answer these, telling Claude what the review found wrong with each"
              >
                Resolve Selected
              </Button>
              <Button
                size="sm"
                disabled={running}
                onClick={() => { onCheckMany(selectedNumbers); setSelectedNumbers([]); }}
                title="Run machine QA on just these questions"
              >
                Quality Check Selected
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedNumbers([])}>
                Clear
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        {visible.map((question) => (
          <QuestionCard
            key={question.number}
            question={question}
            paper={paper}
            onChanged={onChanged}
            expanded={openNumber === question.number}
            onToggle={() => setOpenNumber(openNumber === question.number ? null : question.number)}
            selected={selectedNumbers.includes(question.number)}
            onSelect={filter === "setAside" ? undefined : toggleSelected}
            onResolve={onResolveQuestion}
            onCheck={onCheckQuestion}
            running={running}
          />
        ))}
      </div>

      {visible.length === 0 && (
        <p className="text-sm text-white/50">
          {filter === "pending"  ? "Everything has been reviewed."
           : filter === "setAside" ? "Nothing has been set aside."
                                   : "Nothing in this filter."}
        </p>
      )}
    </Panel>
  );
};

export default ReviewPanel;
